/**
 * 「내 대화 기록」 뷰모델 — 순수 함수만. 화면(`src/pages/LayHistory.jsx`)이 세 보기(시간순 · 질문별 · 받은 편지함)를
 * 그릴 때 쓰는 계산을 한곳에 모았다. 서버 응답 형태는 `docs-site/server/lay-history/index.ts` 머리 주석.
 * 기획: docs/ops/lay-ai-operations-plan.md §3-5 (마) · 시안 docs/design/lay-history-variants.html
 *
 * 규칙
 * - 대화 1건 = lay_conversations 1행 + messages[] + escalations[]. 첫 user 메시지가 「질문」, 첫 assistant 메시지가 「답」.
 * - 운영팀 답변 = escalations[].staff_reply 가 있는 것. 답변 대기 = pending 이면서 staff_reply 없음.
 * - 「답이 갱신됐어요」는 서버가 latest_answer 를 줄 때만 참(지금은 원장이 없어 항상 거짓). 여기서 추측하지 않는다.
 * - 질문별 묶기는 정규화한 질문 문자열이 같을 때만 같은 질문으로 본다(임베딩 클러스터가 생기면 서버가 cluster_id 를 준다).
 */

export const HISTORY_VIEWS = ["timeline", "questions", "inbox"];

export function normalizeQuestion(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[\s　]+/g, " ")
    .replace(/[?？!.。,…~"'“”‘’()[\]{}]/g, "")
    .replace(/(요|나요|까요|죠|죠요|어요|아요|해요|인가요|인가|은가요|는가요)$/g, "")
    .trim();
}

// 출처 slug 접두로 분류. lay_messages.sources 는 [{slug, heading?}] 또는 문자열 배열.
const CATEGORY_BY_PREFIX = [
  [/^c-(tier|fee|billing)/, "등급·멤버십 구독료"],
  [/^c-settle|^c-revenue|^c-payout/, "정산"],
  [/^c-(works|upload|apply|inspect|brand|super|cj)/, "작품·제작"],
  [/^c-channel/, "채널"],
  [/^c-(start|faq|tour)|^cr-/, "시작·기타"],
  [/^r-/, "권리사"],
];
export function categoryFromSources(sources) {
  const list = Array.isArray(sources) ? sources : [];
  for (const s of list) {
    const slug = typeof s === "string" ? s : s && s.slug;
    if (!slug) continue;
    for (const [re, name] of CATEGORY_BY_PREFIX) if (re.test(slug)) return name;
  }
  return "기타";
}

export function sourceLabel(s) {
  if (!s) return "";
  if (typeof s === "string") return s;
  return s.heading ? `${s.slug}#${s.heading}` : (s.title || s.slug || "");
}

/** 대화 한 건의 요약 — 목록 행이 쓰는 필드만. */
export function summarizeConversation(conv) {
  const messages = Array.isArray(conv.messages) ? conv.messages : [];
  const escalations = Array.isArray(conv.escalations) ? conv.escalations : [];
  const firstUser = messages.find((m) => m.role === "user");
  const firstAssistant = messages.find((m) => m.role === "assistant");
  const answered = escalations.filter((e) => e.staff_reply);
  const pending = escalations.filter((e) => e.status === "pending" && !e.staff_reply);
  const resolved = escalations.some((e) => e.status === "resolved");
  const down = messages.some((m) => m.role === "assistant" && m.feedback === "down");
  const up = messages.some((m) => m.role === "assistant" && m.feedback === "up");
  const lastAt = conv.updated_at || (messages.length ? messages[messages.length - 1].created_at : conv.created_at);
  return {
    id: conv.id,
    createdAt: conv.created_at,
    lastAt,
    question: firstUser ? firstUser.content : "",
    answer: firstAssistant ? firstAssistant.content : "",
    sources: firstAssistant && Array.isArray(firstAssistant.sources) ? firstAssistant.sources : [],
    category: categoryFromSources(firstAssistant && firstAssistant.sources),
    hasHuman: answered.length > 0,
    waiting: pending.length > 0 && answered.length === 0,
    resolved,
    down,
    up,
    updatedAnswer: Boolean(conv.latest_answer && conv.latest_answer.changed),
    escalations,
    messages,
  };
}

/** 'today' | 'yesterday' | 'YYYY-MM-DD' — 표시 문구는 화면이 locale 로 만든다. */
export function dayKey(iso, now = Date.now()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  const n = new Date(now);
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(n) - startOf(d)) / 86400000);
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export const TIMELINE_FILTERS = ["all", "human", "updated", "down"];

export function filterSummaries(items, filter, query) {
  const q = String(query || "").trim().toLowerCase();
  return items.filter((s) => {
    if (q && !(`${s.question} ${s.answer}`).toLowerCase().includes(q)) return false;
    if (filter === "human") return s.hasHuman || s.waiting;
    if (filter === "updated") return s.updatedAnswer;
    if (filter === "down") return s.down;
    return true;
  });
}

/** 시간순(A): 최근 것이 위. [{ key, items }] */
export function groupByDay(summaries, now = Date.now()) {
  const sorted = [...summaries].sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
  const groups = [];
  for (const s of sorted) {
    const key = dayKey(s.lastAt, now);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(s);
    else groups.push({ key, items: [s] });
  }
  return groups;
}

/** 질문별(B): 같은 질문(정규화)끼리 묶고 분류로 다시 묶는다. 묶음 안은 최근 물음이 위. */
export function groupByQuestion(summaries) {
  const byKey = new Map();
  for (const s of summaries) {
    const key = normalizeQuestion(s.question) || `#${s.id}`;
    if (!byKey.has(key)) byKey.set(key, { key, question: s.question, category: s.category, asks: [] });
    const g = byKey.get(key);
    g.asks.push(s);
    // 분류는 가장 최근 답의 출처를 따른다 — 같은 질문이 문서 이동으로 분류가 바뀌면 최신을 믿는다
    if (new Date(s.lastAt) >= new Date(g.asks[0].lastAt)) g.category = s.category;
  }
  const questions = [...byKey.values()].map((g) => {
    g.asks.sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
    const latest = g.asks[0];
    return {
      ...g,
      count: g.asks.length,
      latest,
      hasHuman: g.asks.some((a) => a.hasHuman),
      waiting: g.asks.some((a) => a.waiting) && !g.asks.some((a) => a.hasHuman),
      down: g.asks.some((a) => a.down),
      updatedAnswer: g.asks.some((a) => a.updatedAnswer),
    };
  });
  const byCat = new Map();
  for (const q of questions) {
    if (!byCat.has(q.category)) byCat.set(q.category, []);
    byCat.get(q.category).push(q);
  }
  return [...byCat.entries()]
    .map(([category, items]) => ({ category, items: items.sort((a, b) => new Date(b.latest.lastAt) - new Date(a.latest.lastAt)) }))
    .sort((a, b) => new Date(b.items[0].latest.lastAt) - new Date(a.items[0].latest.lastAt));
}

/**
 * 받은 편지함(C): 레이 대화 + 문의 티켓(support_inquiry)을 한 목록으로. 상태는 넷 —
 *   waiting  : 운영팀 답변 대기(에스컬레이션 pending / 티켓 open)
 *   answered : 운영팀 답이 왔고 아직 해결 표시 전(에스컬레이션 staff_reply·resolved 아님 / 티켓 answered)
 *   done     : 해결(에스컬레이션 resolved / 티켓 closed)
 *   self     : 레이가 바로 답한 대화(운영팀 개입 없음)
 * unread 는 화면이 「본 것」 집합(localStorage)으로 정한다 — 여기서는 answered 만 후보로 표시한다.
 */
export const INBOX_TABS = ["all", "waiting", "answered", "done"];

export function inboxStatus(summary) {
  if (summary.resolved) return "done";
  if (summary.hasHuman) return "answered";
  if (summary.waiting) return "waiting";
  return "self";
}

export function ticketStatus(ticket) {
  if (ticket.status === "closed") return "done";
  if (ticket.status === "answered") return "answered";
  return "waiting";
}

export function buildInbox(summaries, tickets) {
  const lay = summaries.map((s) => ({ kind: "lay", id: `lay:${s.id}`, status: inboxStatus(s), at: s.lastAt, title: s.question, preview: s.hasHuman ? (s.escalations.find((e) => e.staff_reply) || {}).staff_reply : s.answer, summary: s }));
  const tk = (Array.isArray(tickets) ? tickets : []).map((t) => ({ kind: "ticket", id: `ticket:${t.id}`, status: ticketStatus(t), at: t.last_message_at || t.created_at, title: t.title, preview: t.body, ticket: t }));
  return [...lay, ...tk].sort((a, b) => new Date(b.at) - new Date(a.at));
}

export function filterInbox(items, tab) {
  if (tab === "waiting") return items.filter((i) => i.status === "waiting");
  if (tab === "answered") return items.filter((i) => i.status === "answered");
  if (tab === "done") return items.filter((i) => i.status === "done");
  return items;
}

/** 진행 단계 인덱스: 0 전달됨 · 1 운영팀 확인 중 · 2 답변 도착 · 3 해결. self 는 null. */
export function inboxStep(status) {
  if (status === "waiting") return 1;
  if (status === "answered") return 2;
  if (status === "done") return 3;
  return null;
}

/** 기록 내려받기 — 개인정보 최소화: 이메일은 넣지 않고 대화·메시지·운영팀 답변만. */
export function buildExport(conversations, exportedAt = new Date().toISOString()) {
  return {
    exported_at: exportedAt,
    source: "laeebly.io — 레이 대화 기록",
    conversations: (Array.isArray(conversations) ? conversations : []).map((c) => ({
      id: c.id,
      created_at: c.created_at,
      messages: (c.messages || []).map((m) => ({ role: m.role, content: m.content, sources: m.sources || [], feedback: m.feedback || null, at: m.created_at })),
      staff_replies: (c.escalations || []).filter((e) => e.staff_reply).map((e) => ({ content: e.staff_reply, at: e.replied_at, status: e.status })),
    })),
  };
}
